import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

const EMPTY = { current: "", next: "", confirm: "" };

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setError(""); };
  const close = (o) => { if (!o) { setF(EMPTY); setError(""); } onOpenChange(o); };

  const save = async () => {
    if (!f.current) return setError("Enter your current password");
    if (f.next.length < 8) return setError("New password must be at least 8 characters");
    if (f.next !== f.confirm) return setError("The new passwords don't match");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      toast.success(data.message);
      close(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-brand" /> Change password</DialogTitle>
          <DialogDescription>At least 8 characters. You'll stay signed in here; other devices will be signed out.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4 py-2" onSubmit={(e) => { e.preventDefault(); save(); }}>
          <div className="space-y-2"><Label className="label-caps text-muted-foreground">Current password</Label>
            <Input type="password" autoComplete="current-password" value={f.current} onChange={set("current")} autoFocus data-testid="cp-current" /></div>
          <div className="space-y-2"><Label className="label-caps text-muted-foreground">New password</Label>
            <Input type="password" autoComplete="new-password" value={f.next} onChange={set("next")} data-testid="cp-new" /></div>
          <div className="space-y-2"><Label className="label-caps text-muted-foreground">Confirm new password</Label>
            <Input type="password" autoComplete="new-password" value={f.confirm} onChange={set("confirm")} data-testid="cp-confirm" /></div>
          {error && <p className="text-sm text-destructive" data-testid="cp-error">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => close(false)} className="rounded-sm" data-testid="cp-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm" data-testid="cp-save">{saving ? "Saving…" : "Change password"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
